import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { Layout, Row, Col, Alert, Tabs } from "antd";
import { EditOutlined, AppstoreOutlined } from "@ant-design/icons";
import { useParams } from "react-router-dom";
import ApplicationDetails from "./ApplicationDetails";
import Roles from "./Roles";
import Registrations from "./Registrations";
import { getActiveApplication } from "../../selectors";
import {
  getApplication,
  getRolesForApplication,
  getRegistrationsForApplication,
  setBreadcrumbs,
} from "../../actions";
import GroupMemberships from "./GroupMemberships";
import styles from "./Application.module.css";
import { RegistrationProtocols } from "../../models/registrationProtocols";
import { FullPageLoader } from "@authzsvc/common-react-components";

const { Content } = Layout;

const tabsRoutes = {
  0: "",
  1: "sso",
  2: "roles",
  3: "memberships",
};

const Application = (props) => {
  const { appid, tab = "" } = useParams();
  const [loading, setLoading] = useState(true);

  const navigate = useNavigate();

  const basePathname = `/manage/${appid}`;
  const currentTabKey =
    Object.entries(tabsRoutes).find(([, route]) => route === tab)?.[0] ?? "0";

  const {
    activeApplication,
    setBreadcrumbs,
    getApplication,
    getRolesForApplication,
    getRegistrationsForApplication,
  } = props;
  const [hasOpenIdRegistration, setHasOpenIdRegistration] = useState(false);
  const [editMode, setEditMode] = useState(false);

  useEffect(() => {
    const fetchApp = async () => {
      setLoading(true);
      await getApplication(appid);
      setLoading(false);
    };

    fetchApp();

    setBreadcrumbs([
      ["/", "Applications"],
      [
        `/manage/${appid}`,
        activeApplication?.application?.displayName || "Application",
      ],
    ]);
  }, [
    activeApplication?.application?.displayName,
    appid,
    getApplication,
    setBreadcrumbs,
  ]);

  useEffect(() => {
    if (activeApplication?.application?.id) {
      const id = activeApplication.application.id;
      getRolesForApplication(id);
      getRegistrationsForApplication(id);
    }
  }, [
    activeApplication.application.id,
    getRegistrationsForApplication,
    getRolesForApplication,
  ]);

  useEffect(() => {
    const hasOIDC = activeApplication?.registrations?.some(
      (r) => r.providerIdentifier === RegistrationProtocols.OIDC,
    );
    setHasOpenIdRegistration(hasOIDC);
  }, [activeApplication?.registrations]);

  const handleTabChange = (key) => {
    const route = tabsRoutes[key];
    navigate(route ? `${basePathname}/${route}` : basePathname);
  };

  return loading ? (
    <FullPageLoader />
  ) : !activeApplication?.application?.id ? (
    <Content style={{ padding: "24px", marginTop: "32px" }}>
      <Row>
        <Col span={24}>
          <Alert
            type="error"
            message={`Failed to load application ${appid}`}
            description={
              <ul>
                <li>
                  If you followed a link, ensure the application identifier is
                  valid.
                </li>
              </ul>
            }
            showIcon
          />
        </Col>
      </Row>
    </Content>
  ) : (
    <Content style={{ padding: "24px" }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "10px",
          marginBottom: "16px",
        }}
      >
        {editMode ? (
          <EditOutlined style={{ fontSize: 32 }} />
        ) : (
          <AppstoreOutlined style={{ fontSize: 32 }} />
        )}
        <h2 style={{ margin: 0 }}>
          Application: {activeApplication.application.displayName}
        </h2>
      </div>

      <Tabs
        activeKey={currentTabKey}
        onChange={handleTabChange}
        type="line"
        className={styles.wrapped}
        items={[
          {
            key: "0",
            label: "Application details",
            children: (
              <ApplicationDetails
                {...activeApplication}
                editMode={editMode}
                setEditMode={setEditMode}
              />
            ),
          },
          {
            key: "1",
            label: "SSO Registration",
            children: <Registrations {...activeApplication} />,
          },
          {
            key: "2",
            label: "Roles",
            children: <Roles {...activeApplication} />,
          },
          ...(hasOpenIdRegistration
            ? [
                {
                  key: "3",
                  label: "Group memberships",
                  children: <GroupMemberships {...activeApplication} />,
                },
              ]
            : []),
        ]}
      />
    </Content>
  );
};

export default connect(
  (state) => ({
    activeApplication: getActiveApplication(state),
  }),
  {
    getApplication,
    setBreadcrumbs,
    getRolesForApplication,
    getRegistrationsForApplication,
  },
)(Application);

Application.propTypes = {
  activeApplication: PropTypes.object,
  breadcrumbs: PropTypes.array,
  getApplication: PropTypes.func,
  setBreadcrumbs: PropTypes.func,
  getRolesForApplication: PropTypes.func,
  getRegistrationsForApplication: PropTypes.func,
};
